import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface AdConnectionSummary {
  connectionId: string;
  provider: 'meta_ads' | 'google_ads';
  status: string;
  adAccountId: string;
  connectedPageIds: string[];
  customerIds: string[];
  lastSyncAt: string | null;
  lastSyncResult: string | null;
  createdAt: string | null;
}

interface State {
  connections: AdConnectionSummary[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  disconnect: (connectionId: string) => Promise<void>;
  rotateWebhookToken: () => Promise<string>;
}

export function useAdConnections(companyId: string | undefined): State {
  const [connections, setConnections] = useState<AdConnectionSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const fn = httpsCallable<
      { action: 'listConnections'; companyId: string },
      { connections: AdConnectionSummary[] }
    >(functions, 'adAction');
    setLoading(true);
    setError(null);
    fn({ action: 'listConnections', companyId })
      .then((r) => setConnections(r.data.connections))
      .catch((err) => {
        console.error('[useAdConnections] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, refreshKey]);

  const disconnect = useCallback(
    async (connectionId: string) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        {
          action: 'disconnectConnection';
          companyId: string;
          connectionId: string;
        },
        { success: boolean }
      >(functions, 'adAction');
      await fn({ action: 'disconnectConnection', companyId, connectionId });
      refresh();
    },
    [companyId, refresh]
  );

  const rotateWebhookToken = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    const fn = httpsCallable<
      { action: 'rotateWebhookToken'; companyId: string },
      { success: boolean; metaVerifyToken: string }
    >(functions, 'adAction');
    const result = await fn({ action: 'rotateWebhookToken', companyId });
    return result.data.metaVerifyToken;
  }, [companyId]);

  return {
    connections,
    loading,
    error,
    refresh,
    disconnect,
    rotateWebhookToken,
  };
}
